import { Helmet } from "react-helmet";
import { FeaturesSection } from "@/components/FeaturesSection";
import { StatsSection } from "@/components/StatsSection";
import { OptimizedCyberpunkReviewsSection } from "@/components/OptimizedCyberpunkReviewsSection"; // Using optimized non-WebGL cyberpunk version
import { CyberpunkBackground } from "@/components/CyberpunkBackground";
import { PricingSection } from "@/components/PricingSection";
// Import the component that's actually exported
import { FaqSection } from "@/components/FaqSection";
import { MainNavigation } from "@/components/MainNavigation";
import { MainFooter } from "@/components/MainFooter";
import { SecurityFeaturesSection } from "@/components/SecurityFeaturesSection";
import { SEOMeta } from "@/components/SEOMeta";
import { defaultSEO } from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { SolanaHeroSection } from "@/components/SolanaHeroSection";
import { ThreeDHeroSection } from "@/components/3DHeroSection";
import { TrendingPlatformsSection } from "@/components/TrendingPlatformsSection";
import { SolanaDemoShowcase } from "@/components/SolanaDemoShowcase";
import { AIAssistant } from "@/components/AIAssistant";
import { Bot, List, Cpu } from "lucide-react";
import { useState, useRef, useEffect, Suspense, lazy } from "react";
import { ContactSection } from "@/components/ContactSection";
import { ToolsSection } from "@/components/ToolsSection";
import { ApiSection } from "@/components/ApiSection";
import { LegalSections } from "@/components/LegalSections";
import { ResourcesSection } from "@/components/ResourcesSection";
import { SimpleSolanaMascot } from "@/components/SimpleSolanaMascot";
import { MemeCoinsIndicator } from "@/components/MemeCoinsIndicator";
import { LivePriceUpdates } from "@/components/LivePriceUpdates";
import { AITradingAssistant } from "@/components/AITradingAssistant";
import { FloatingNavbar } from "@/components/FloatingNavbar";
import { 
  ArrowRight, Monitor, Shield, Wallet, TrendingUp
} from "lucide-react";
import { SiSolana } from "react-icons/si";
// CornerMascot component removed as per user request

// Import feature content for modals
// import { FeatureDetailModal } from "@/components/FeatureDetailModal";
import { 
  volumeGenerationContent,
  realTimeMonitoringContent,
  antiDetectionContent,
  multiWalletContent
} from "@/lib/featureDetailsContent";

// Animated features section with 3D cards
const SolanaFeaturesSection = () => {
  // State for tracking which feature's modal is open
  const [activeFeatureModal, setActiveFeatureModal] = useState<string | null>(null);
  const FeatureDetailModal = lazy(() => import("@/components/FeatureDetailModal").then(mod => ({ default: mod.FeatureDetailModal })));

  const features: {
    icon: React.ReactNode;
    title: string;
    description: string;
    gradientFrom: string;
    gradientTo: string;
    modalContent: any;
  }[] = [
    {
      icon: <TrendingUp className="h-10 w-10 text-white" />,
      title: "Generate Trading Volume",
      description: "Our Solana Volume Bot creates organic-looking trading volume with distributed buy and sell orders to get your token noticed on DEXTools, Pump Fun, and Dexscreener.",
      gradientFrom: "#9945FF",
      gradientTo: "#14F195",
      modalContent: volumeGenerationContent
    },
    {
      icon: <Monitor className="h-10 w-10 text-white" />,
      title: "Real-Time Monitoring",
      description: "Track your Solana token's performance across all major DEX platforms with real-time analytics provided by the Solana Volume Bot.",
      gradientFrom: "#14F195",
      gradientTo: "#03E1FF",
      modalContent: realTimeMonitoringContent
    },
    {
      icon: <Shield className="h-10 w-10 text-white" />,
      title: "Anti-Detection Technology",
      description: "The Solana Volume Bot utilizes advanced anti-detection algorithms to create natural trading patterns that avoid detection by monitoring systems on platforms like Pump Fun and DEXTools.",
      gradientFrom: "#03E1FF",
      gradientTo: "#DC1FFF",
      modalContent: antiDetectionContent
    },
    {
      icon: <Wallet className="h-10 w-10 text-white" />,
      title: "Multi-Wallet Support",
      description: "Connect multiple wallets to the Solana Volume Bot to create realistic, distributed trading activity and significant volume across the blockchain, enhancing your token's presence on DEXTools and Pump Fun.",
      gradientFrom: "#DC1FFF",
      gradientTo: "#9945FF",
      modalContent: multiWalletContent
    }
  ];

  // Handle opening a specific feature modal
  const openFeatureModal = (title: string) => {
    setActiveFeatureModal(title);
  };

  // Handle closing the feature modal
  const closeFeatureModal = () => {
    setActiveFeatureModal(null);
  };

  // Find the active feature for the modal
  const activeFeature = features.find(feature => feature.title === activeFeatureModal);

  return (
    <section className="py-20 relative overflow-hidden">
      {/* Cyberpunk background */}
      <CyberpunkBackground variant="dark" withGrid withNoise withGlitch />
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4 text-white">
            Powerful <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#14F195] to-[#9945FF]">Features</span>
          </h2>
          <p className="text-gray-200 max-w-2xl mx-auto text-lg">
            Our advanced toolkit gives you everything you need to boost your Solana token's visibility on major tracking platforms
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="relative group"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-[#1A1A25] to-[#0D0D15] rounded-xl transform group-hover:scale-[1.02] transition-transform duration-300"></div>
              <div className="absolute inset-[1px] bg-[#0F0F17] rounded-[10px] z-10"></div>

              {/* Glow effect on hover */}
              <div 
                className="absolute -inset-0.5 bg-gradient-to-r rounded-xl opacity-0 group-hover:opacity-70 blur-md transition-opacity duration-300 z-0"
                style={{ 
                  backgroundImage: `linear-gradient(to right, ${feature.gradientFrom}, ${feature.gradientTo})` 
                }}
              ></div>

              <div className="relative z-20 p-6 h-full flex flex-col">
                <div 
                  className="w-16 h-16 rounded-lg flex items-center justify-center mb-5"
                  style={{ 
                    backgroundImage: `linear-gradient(135deg, ${feature.gradientFrom}, ${feature.gradientTo})` 
                  }}
                >
                  {feature.icon}
                </div>

                <h3 className="text-xl font-bold mb-3 text-white">{feature.title}</h3>

                <p className="text-gray-200 mb-4 flex-grow">{feature.description}</p>

                <button 
                  onClick={() => openFeatureModal(feature.title)}
                  className="flex items-center text-accent text-sm font-medium cursor-pointer"
                  aria-label={`Learn more about ${feature.title}`}
                >
                  Learn more
                  <ArrowRight className="ml-1 h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
        
        {/* Feature Detail Modal */}
        {activeFeature && (
          <Suspense fallback={null}>
            <FeatureDetailModal
              isOpen={!!activeFeatureModal}
              onClose={closeFeatureModal}
              content={{
                title: activeFeature.title,
                description: activeFeature.modalContent.description,
                icon: activeFeature.icon,
              }}
            />
          </Suspense>
        )}
      </div>
    </section>
  );
};

const LegalPopup = lazy(() => import("@/components/LegalPopup").then(mod => ({ default: mod.LegalPopup })));
const AIVolumeAssistantPopup = lazy(() => import("@/components/AIVolumeAssistantPopup"));

const TableOfContents = lazy(() => import("@/components/TableOfContents").then(mod => ({ default: mod.TableOfContents })));

export default function Home() {
  // State for legal popups
  const [legalPopup, setLegalPopup] = useState<{
    isOpen: boolean;
    type: 'terms' | 'privacy' | 'disclaimer' | 'dmca';
  }>({
    isOpen: false,
    type: 'terms'
  });
  
  // Function to scroll to section
  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    }
  };
  
  // State for AI Volume Assistant popup
  const [isAIAssistantOpen, setIsAIAssistantOpen] = useState(false);
  
  // State for Table of Contents
  const [isTableOfContentsOpen, setIsTableOfContentsOpen] = useState(false); // Always default to false
  
  // Ref for pricing CTA section
  const pricingRef = useRef<HTMLDivElement>(null);
  
  // Function to open legal popup
  const openLegalPopup = (type: 'terms' | 'privacy' | 'disclaimer' | 'dmca') => {
    setLegalPopup({
      isOpen: true,
      type
    });
  };

  // Function to close legal popup
  const closeLegalPopup = () => {
    setLegalPopup(prev => ({
      ...prev,
      isOpen: false
    }));
  };
  
  // Function to handle game end - highlight CTA
  const handleGameEnd = () => {
    // Add a subtle highlight to the pricing section
    if (pricingRef.current) {
      pricingRef.current.classList.add('highlight-section');
      
      // Remove highlight class after animation completes
      setTimeout(() => {
        if (pricingRef.current) {
          pricingRef.current.classList.remove('highlight-section');
        }
      }, 2000);
    }
  };
  
  // Function to scroll to CTA
  const scrollToCTA = () => {
    if (pricingRef.current) {
      pricingRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'center'
      });
    }
  };

  const [canonicalUrl, setCanonicalUrl] = useState<string>(defaultSEO.canonicalUrl);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setCanonicalUrl(window.location.origin + window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const scrollTo = params.get("scrollTo");
    if (scrollTo) {
      setTimeout(() => {
        const section = document.getElementById(scrollTo);
        if (section) {
          section.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 200);
    }
  }, []);

  return (
    <>
      <SEOMeta 
        title="Solana Volume Bot: SOL Volume Bot - Pump Fun Volume Bot"
        description={defaultSEO.description}
        keywords={defaultSEO.keywords}
        ogImage={defaultSEO.ogImage}
        ogUrl={defaultSEO.ogUrl}
        canonicalUrl={canonicalUrl}
      />
      <Helmet>
        {/* Google tag (gtag.js) */}
        <script async src="https://www.googletagmanager.com/gtag/js?id=AW-17061710943"></script>
        <script>{`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'AW-17061710943');
        `}</script>
      </Helmet>
      <div className="flex flex-col min-h-screen">
        {/* <MainNavigation /> Removed to prevent duplicate navigation */}
        <main className="flex-1" role="main" aria-label="Solana Volume Bot features and information">
          {/* A.I. Volume Bot Help Assistant Popup */}
          {isAIAssistantOpen && (
            <Suspense fallback={null}>
              <AIVolumeAssistantPopup
                isOpen={isAIAssistantOpen}
                onClose={() => setIsAIAssistantOpen(false)}
              />
            </Suspense>
          )}
          
          {/* Table of Contents Popup */}
          {isTableOfContentsOpen && (
            <Suspense fallback={null}>
              <TableOfContents
                isVisible={isTableOfContentsOpen}
                onClose={() => setIsTableOfContentsOpen(false)}
              />
            </Suspense>
          )}
          
          {/* Solana Mascot */}
          <SimpleSolanaMascot 
            position="bottom-right"
            ctaText="Start Free Trial"
            autoHide={false}
            marketSentiment="bullish"
          />
          
          {/* A.I. Assistant - Removed to prevent duplicates and CPU usage issues */}
          
          {/* New 3D-style Hero Section */}
          <div id="hero">
            <ThreeDHeroSection />
          </div>
          
          {/* Trending Platforms Section */}
          <div id="trending-platforms">
            <TrendingPlatformsSection />
          </div>

          {/* Features Section with 3D cards */}
          <div id="features">
            <SolanaFeaturesSection />
          </div>

          {/* Demo Showcase Section */}
          <div id="demo">
            <SolanaDemoShowcase />
          </div>

          {/* Removed Platform Showcase Section as requested */}

          {/* Tools Section Container */}
          <div id="tools">
            <ToolsSection />
          </div>
          
          {/* API Section */}
          <div id="api">
            <ApiSection />
          </div>

          {/* Sentiment Analysis & Tools Container */}
          {/* Removed Live Sentiment Analysis section as requested */}

          {/* Reviews Section with 3D Cyberpunk Avatars (Optimized) */}
          <div id="reviews">
            <OptimizedCyberpunkReviewsSection />
          </div>

          {/* Pricing Section - with ref for game targeting */}
          <div id="pricing" ref={pricingRef}>
            <PricingSection />
          </div>

          {/* FAQ Section */}
          <div id="faqs">
            <FaqSection />
          </div>
          
          {/* Security Features Section */}
          <div id="security">
            <SecurityFeaturesSection />
          </div>

          {/* Resources Section with Documentation, API Reference, and Tutorials */}
          <div id="resources">
            <ResourcesSection />
          </div>

          {/* Contact Section with Social Media */}
          <div id="contact">
            <ContactSection />
          </div>

          {/* Legal Sections directly on landing page */}
          <div id="legal">
            <LegalSections />
          </div>
        </main>

        {/* Fixed navigation buttons container - better mobile experience with glassmorphism */}
        <div className="fixed z-40 flex flex-col gap-3 sm:gap-4 pointer-events-none">
          {/* Left-side Table of Contents button */}
          <div className="fixed bottom-6 left-6 pointer-events-auto">
            <button 
              onClick={() => setIsTableOfContentsOpen(true)}
              className="flex items-center gap-2 bg-black/85 backdrop-blur-sm rounded-xl px-3 py-2.5 border border-[#14F195]/30 shadow-lg hover:shadow-[0_0_15px_rgba(20,241,149,0.3)] transition-all duration-300 group"
              aria-label="Open Table of Contents"
              role="button"
            >
              <div className="relative w-5 h-5 flex items-center justify-center">
                <List className="h-4 w-4 text-[#14F195]" />
              </div>
              {/* "Table of Contents" text removed as requested */}
            </button>
          </div>
          
          {/* Right-side AI Volume Assistant button - maximum z-index to guarantee top layer positioning */}
          <div className="fixed bottom-6 right-7 pointer-events-auto z-[999]">
            <button 
              onClick={() => setIsAIAssistantOpen(true)}
              className="flex items-center gap-2 bg-black/85 backdrop-blur-sm rounded-xl px-3 py-2.5 border border-[#14F195]/30 shadow-lg hover:shadow-[0_0_15px_rgba(20,241,149,0.3)] transition-all duration-300 group"
              aria-label="Open AI Volume Bot Assistant"
            >
              <div className="relative w-5 h-5 flex items-center justify-center">
                <Bot className="h-4 w-4 text-[#14F195] absolute animate-pulse" />
                <div className="absolute -top-1 -right-1 w-2 h-2 bg-[#14F195] rounded-full animate-ping opacity-75"></div>
                <div className="absolute -top-1 -right-1 w-2 h-2 bg-[#14F195] rounded-full"></div>
              </div>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#14F195] to-[#9945FF] font-medium text-sm md:text-base hidden sm:inline">AI Volume Bot Assistant</span>
            </button>
          </div>
        </div>

        {/* Footer with Legal Links */}
        <MainFooter />
      </div>

      {/* Dynamic Legal Popups */}
      {legalPopup.isOpen && (
        <Suspense fallback={null}>
          <LegalPopup
            isOpen={legalPopup.isOpen}
            onClose={closeLegalPopup}
            contentType={legalPopup.type}
          />
        </Suspense>
      )}
      
      {/* Popups are handled with AnimatePresence in the main section */}
      
      {/* Solana Mascot - shows only the pop-up box, black square and logo removed */}
      <SimpleSolanaMascot 
        position="bottom-right"
        ctaText="Start Free Trial"
        ctaLink="#pricing"
        hideHamburgerButton={true}
      />

      {/* SEO-Optimized Table of Contents (Hidden but parsed by search engines) */}
      <div className="sr-only">
        <h2>Solana Volume Bot - Table of Contents</h2>
        <p>
          The <a href="https://solanavolumebot.io" rel="dofollow">Solana Volume Bot</a> is an advanced tool for generating strategic 
          trading volume on Solana tokens, helping projects gain visibility 
          on major DEX trackers and attract serious traders and investors.
        </p>
        <ol>
          <li><a href="#hero">Introduction to Solana Volume Bot</a></li>
          <li><a href="#features">Key Features and Capabilities</a></li>
          <li><a href="#demo">Live Demo and Showcase</a></li>
          <li><a href="#reviews">User Testimonials and Reviews</a></li>
          <li><a href="#pricing">Pricing Plans and Packages</a></li>
          <li><a href="#faqs">Frequently Asked Questions</a></li>
          <li><a href="#resources">Resources & Documentation</a></li>
          <li><a href="#contact">Contact Information</a></li>
          <li><a href="#legal">Legal Information (Terms, Privacy Policy & DMCA)</a></li>
        </ol>
      </div>

      {/* Performance optimizations - no external images needed anymore as we use SVGs */}

      {/* Resource hints for browsers */}
      <meta httpEquiv="x-dns-prefetch-control" content="on" />

      {/* Global SEO structured data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify([
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Solana Volume Bot - Advanced Volume Generator",
            "applicationCategory": "DeFiApplication",
            "operatingSystem": "Web",
            "offers": {
              "@type": "Offer",
              "price": "499.00",
              "priceCurrency": "USD"
            },
            "description": "Advanced volume generation tool that gets your Solana token trending on major DEX trackers. Boost visibility with AI-powered trading patterns.",
            "keywords": "solana volume bot, DEX trackers, trading volume, token visibility, crypto trending, solana token, pump.fun, volume generation",
            "applicationSubCategory": "Blockchain",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "https://solanavolumebot.io"
            },
            "provider": {
              "@type": "Organization",
              "name": "Solana Volume Bot",
              "url": "https://solanavolumebot.io",
              "sameAs": [
                "https://twitter.com/solanavolumebot",
                "https://discord.gg/solanavolumebot",
                "https://t.me/solanavolumebot" 
              ]
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Solana Volume Bot",
            "description": "The #1 volume generation solution for Solana tokens. Make your token trend on DEXTools and PUMP.FUN with our advanced volume bot.",
            "brand": {
              "@type": "Brand",
              "name": "Sol Volume Bot"
            },
            "offers": [
              {
                "@type": "Offer",
                "name": "Starter Plan", 
                "price": "199.00",
                "priceCurrency": "USD",
                "availability": "https://schema.org/InStock"
              },
              {
                "@type": "Offer",
                "name": "Pro Plan",
                "price": "399.00",
                "priceCurrency": "USD",
                "availability": "https://schema.org/InStock"
              },
              {
                "@type": "Offer",
                "name": "Enterprise Plan",
                "price": "499.00", 
                "priceCurrency": "USD",
                "availability": "https://schema.org/InStock"
              }
            ],
            "review": [
              {
                "@type": "Review",
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": "5",
                  "bestRating": "5"
                },
                "author": {
                  "@type": "Person",
                  "name": "Alex Thompson"
                },
                // removed date for anonymity
                "reviewBody": "The PUMP.FUN Trending Bot was a game-changer for our token launch. We went from zero visibility to trending on multiple DEX platforms within 48 hours."
              },
              {
                "@type": "Review",
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": "5",
                  "bestRating": "5"
                },
                "author": {
                  "@type": "Person",
                  "name": "Samantha Chen"
                },
                // removed date for anonymity
                "reviewBody": "We tried several solutions before finding the DEXTools Volume Bot. Nothing else comes close in terms of effectiveness and reliability. Our token has maintained consistent visibility for weeks."
              },
              {
                "@type": "Review",
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": "4",
                  "bestRating": "5"
                },
                "author": {
                  "@type": "Person",
                  "name": "Michael Rodriguez"
                },
                // removed date for anonymity
                "reviewBody": "The Solana Volume Bot customer support team is excellent. They helped us optimize our settings to match our token's specific needs and market conditions."
              }
            ],
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.8",
              "bestRating": "5",
              "worstRating": "1",
              "ratingCount": "149",
              "reviewCount": "149"
            }
          }
        ])
      }} />
    </>
  );
}